import React, { useCallback, useRef } from 'react';
import PhotoLandscape from '@uber/base-icons/photo-landscape';
import { type Theme, useStyletron } from 'baseui';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { REFERENCE_IMAGE } from '../../../constants/template-builder';
type ImageUploadButtonPropsT = {
  onImageSelect: (imageUrl: string) => void;
};
const getContainerStyles = (theme: Theme) => ({
  position: ('absolute' as const),
  bottom: theme.sizing.scale600,
  right: theme.sizing.scale600,
  pointerEvents: ('auto' as const)
});
const BUTTON_OVERRIDES = {
  BaseButton: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      width: $theme.sizing.scale1200,
      height: $theme.sizing.scale1200,
      minWidth: $theme.sizing.scale1200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
      backgroundColor: $theme.colors.backgroundPrimary,
      color: $theme.colors.contentPrimary,
      borderRadius: $theme.borders.radiusCircular,
      boxShadow: $theme.lighting.shadow400,
      ':hover': {
        backgroundColor: $theme.colors.backgroundSecondary
      }
    })
  }
};
export function ImageUploadButton({
  onImageSelect
}: ImageUploadButtonPropsT) {
  const [css, theme] = useStyletron();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      onImageSelect(url);
    }
    e.target.value = '';
  }, [onImageSelect]);
  return <div className={css(getContainerStyles(theme))}>
      <input ref={fileInputRef} type="file" accept={REFERENCE_IMAGE.ACCEPTED_TYPES} onChange={handleFileChange} style={{
      display: 'none'
    }} data-testid="image-upload-input" />
      <StatefulTooltip content={REFERENCE_IMAGE.UPLOAD_TOOLTIP} placement={PLACEMENT.left} showArrow>
        <Button data-testid="image-upload-button" onClick={handleClick} kind={KIND.tertiary} size={SIZE.medium} shape={SHAPE.circle} overrides={BUTTON_OVERRIDES}>
          <PhotoLandscape size={REFERENCE_IMAGE.ICON_SIZE} />
        </Button>
      </StatefulTooltip>
    </div>;
}